import React, { useCallback, useMemo, useState } from 'react';
import {
    App,
    Button,
    Card,
    Descriptions,
    Input,
    Space,
    Table,
    Tag,
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    ExportOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    GET_PORTAL_BILL_DETAIL,
    GET_PORTAL_BILL_EXPORT_DOWNLOAD_URL,
    GET_PORTAL_BILL_EXPORT_HISTORY,
} from 'graphql/queries/bills';
import { PORTAL_CONFIRM_BILL, PORTAL_EXPORT_BILL, PORTAL_REJECT_BILL } from 'graphql/mutations/bills';
import {
    PagedPortalBillExportRecord,
    PortalBillDetail,
    PortalBillExportRecord,
    PortalBillFeeSummary,
} from 'graphql/types';
import { usePermission } from 'components/PermissionProvider';
import { getPortalBillStatusLabel } from 'utils/portalBillStatusLabel';
import BillExportProgressModal from 'components/BillExportProgressModal';

const formatDateTime = (value: string | null | undefined, timeZone: string) => {
    if (!value) return '-';
    try {
        return new Intl.DateTimeFormat('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: timeZone || 'UTC',
        }).format(new Date(value));
    } catch {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
    }
};

const BillDetailPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { modal, message } = App.useApp();
    const navigate = useNavigate();
    const { billId } = useParams<{ billId: string }>();
    const { hasPermission } = usePermission();
    const [exportOpen, setExportOpen] = useState(false);
    const [exportTaskIds, setExportTaskIds] = useState<string[]>([]);
    const [exportPage, setExportPage] = useState(0);
    const exportPageSize = 10;

    const { data, loading, refetch } = useQuery<{ portalBillDetail: PortalBillDetail }>(
        GET_PORTAL_BILL_DETAIL,
        {
            variables: { billId },
            skip: !billId,
            fetchPolicy: 'network-only',
        },
    );

    const { data: exportHistoryData, loading: exportHistoryLoading, refetch: refetchExportHistory } =
        useQuery<{ portalBillExportHistory: PagedPortalBillExportRecord }>(
            GET_PORTAL_BILL_EXPORT_HISTORY,
            {
                variables: { billId, page: exportPage, size: exportPageSize },
                skip: !billId,
                fetchPolicy: 'network-only',
            },
        );

    const [fetchDownloadUrl] = useLazyQuery<{ portalBillExportDownloadUrl: string }>(
        GET_PORTAL_BILL_EXPORT_DOWNLOAD_URL,
    );

    const [confirmBill] = useMutation(PORTAL_CONFIRM_BILL);
    const [rejectBill] = useMutation(PORTAL_REJECT_BILL);
    const [exportBill, { loading: exporting }] = useMutation(PORTAL_EXPORT_BILL);

    const bill = data?.portalBillDetail;
    const canOperate = bill?.billStatus === '4';
    const billTimeZone = bill?.timeZone || 'UTC';

    const formatTime = useCallback(
        (value: string | null | undefined) => formatDateTime(value, billTimeZone),
        [billTimeZone],
    );

    const handleRefreshExportHistory = useCallback(async () => {
        await refetchExportHistory();
    }, [refetchExportHistory]);

    const handleDownloadExport = useCallback(async (taskId: string) => {
        const { data: downloadData } = await fetchDownloadUrl({ variables: { taskId } });
        const url = downloadData?.portalBillExportDownloadUrl;
        if (!url) {
            message.error(t('billExport.downloadFailed'));
            return;
        }
        window.open(url, '_blank', 'noreferrer');
    }, [fetchDownloadUrl, t]);

    const feeColumns = useMemo(
        () => [
            {
                title: t('billDetail.feeItemName'),
                dataIndex: 'feeItemName',
                key: 'feeItemName',
                width: 220,
                ellipsis: true,
            },
            {
                title: t('billDetail.feeAmount'),
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                width: 160,
                render: (amount: string, record: PortalBillFeeSummary) => {
                    const isNegative = record.amountSign === '-' || record.amountSign === '-1';
                    return (
                        <span style={{ color: isNegative ? '#ff4d4f' : '#52c41a', whiteSpace: 'nowrap' }}>
                            {`${isNegative ? '-' : '+'}${amount} ${bill?.currency || ''}`}
                        </span>
                    );
                },
            },
        ],
        [bill?.currency, t],
    );

    const exportRecordColumns = useMemo(
        () => [
            {
                title: t('billDetail.exportHistoryColumns.exportTime'),
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 180,
                render: (value: string) => formatTime(value),
            },
            {
                title: t('billDetail.exportHistoryColumns.fileName'),
                dataIndex: 'fileName',
                key: 'fileName',
                ellipsis: true,
                render: (value: string | null | undefined) => value || '-',
            },
            {
                title: t('billDetail.exportHistoryColumns.status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status: string) => {
                    const color =
                        status === 'SUCCESS' ? 'green' :
                        status === 'FAILED' ? 'red' :
                        status === 'RUNNING' ? 'blue' : 'default';
                    return <Tag color={color}>{t(`billDetail.exportStatus.${status}`, status)}</Tag>;
                },
            },
            {
                title: t('billDetail.exportHistoryColumns.actions'),
                key: 'actions',
                width: 120,
                render: (_: unknown, record: PortalBillExportRecord) => (
                    <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        disabled={record.status !== 'SUCCESS'}
                        onClick={() => handleDownloadExport(record.taskId)}
                    >
                        {t('billExport.download')}
                    </Button>
                ),
            },
        ],
        [formatTime, handleDownloadExport, t],
    );

    const handleConfirm = () => {
        modal.confirm({
            title: t('billDetail.confirmTitle'),
            content: t('billDetail.confirmContent'),
            okText: t('billDetail.confirmOk'),
            cancelText: t('billDetail.confirmCancel'),
            onOk: async () => {
                const result = await confirmBill({ variables: { billId, remark: null } });
                if (result.data?.portalConfirmBill?.success) {
                    message.success(t('billDetail.confirmSuccess'));
                    await refetch();
                } else {
                    message.error(result.data?.portalConfirmBill?.errorMessage || t('billDetail.confirmFailed'));
                }
            },
        });
    };

    const handleReject = () => {
        let remark = '';
        modal.confirm({
            title: t('billDetail.rejectTitle'),
            content: (
                <div className="pb-4">
                    <p className="mb-2">{t('billDetail.rejectInstruction')}</p>
                    <Input.TextArea
                        rows={4}
                        maxLength={500}
                        showCount
                        onChange={(e) => {
                            remark = e.target.value;
                        }}
                    />
                </div>
            ),
            okText: t('billDetail.rejectOk'),
            cancelText: t('billDetail.rejectCancel'),
            okType: 'danger',
            onOk: async () => {
                if (!remark.trim()) {
                    message.warning(t('billDetail.rejectReasonRequired'));
                    return Promise.reject(new Error('remark required'));
                }
                const result = await rejectBill({
                    variables: { billId, remark: remark.trim() },
                });
                if (result.data?.portalRejectBill?.success) {
                    message.success(t('billDetail.rejectSuccess'));
                    await refetch();
                } else {
                    message.error(result.data?.portalRejectBill?.errorMessage || t('billDetail.rejectFailed'));
                    return Promise.reject(new Error('reject failed'));
                }
            },
        });
    };

    const handleExport = async () => {
        if (!bill) return;
        const result = await exportBill({
            variables: {
                billId: bill.billId,
                template: bill.exportTemplate || 'GENERAL_TEMPLATE',
                format: 'EXCEL',
                language: i18n.language?.toUpperCase().startsWith('ZH') ? 'ZH' : 'EN',
            },
        });
        if (result.data?.portalExportBill?.success) {
            const taskIds = result.data.portalExportBill.taskIds || [];
            if (taskIds.length === 0) {
                message.error(t('billDetail.exportFailed'));
                return;
            }
            setExportTaskIds(taskIds);
            setExportOpen(true);
            await refetchExportHistory();
        } else {
            message.error(result.data?.portalExportBill?.errorMessage || t('billDetail.exportFailed'));
        }
    };

    if (!billId) {
        return null;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/bill-list')}>
                    {t('billDetail.back')}
                </Button>
                <Space wrap>
                    {hasPermission('portal:bill-list:export') && (
                        <Button
                            icon={<ExportOutlined />}
                            loading={exporting}
                            onClick={handleExport}
                        >
                            {t('billList.actions.exportDetails')}
                        </Button>
                    )}
                    {canOperate && hasPermission('portal:bill-list:reject') && (
                        <Button danger icon={<CloseCircleOutlined />} onClick={handleReject}>
                            {t('billDetail.rejectBill')}
                        </Button>
                    )}
                    {canOperate && hasPermission('portal:bill-list:confirm') && (
                        <Button type="primary" icon={<CheckOutlined />} onClick={handleConfirm}>
                            {t('billDetail.confirmBill')}
                        </Button>
                    )}
                </Space>
            </div>

            <Card loading={loading} title={t('billDetail.title')}>
                {bill && (
                    <>
                        <Descriptions column={2} bordered size="small">
                            <Descriptions.Item label={t('billList.columns.clientName')} span={2}>
                                {bill.clientName}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('billList.columns.billId')}>{bill.billId}</Descriptions.Item>
                            <Descriptions.Item label={t('billList.columns.billType')}>{bill.billTypeLabel}</Descriptions.Item>
                            <Descriptions.Item label={`${t('billList.columns.billTimeRange')} (${bill.timeZone})`}>
                                {formatDateTime(bill.periodStart, bill.timeZone)} ~{' '}
                                {formatDateTime(bill.periodEnd, bill.timeZone)}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('billList.columns.timeZone')}>{bill.timeZone || '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('billList.columns.billStatus')}>
                                <Tag>
                                    {getPortalBillStatusLabel(bill.billStatus, t, bill.billStatusLabel)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('billList.columns.orderCount')}>{bill.orderCount}</Descriptions.Item>
                        </Descriptions>

                        <div className="mt-6 rounded border border-gray-200 bg-gray-50 px-4 py-3">
                            <div className="text-sm text-gray-500">{t('billDetail.totalAmountTitle')}</div>
                            <div className="text-2xl font-semibold text-red-600">
                                {bill.billAmount} {bill.currency}
                            </div>
                        </div>
                    </>
                )}
            </Card>

            {bill && (
                <Card title={t('billDetail.feeSummaryCardTitle')}>
                    <Table<PortalBillFeeSummary>
                        rowKey={(record) => record.feeItemId}
                        columns={feeColumns}
                        dataSource={bill.feeSummaries || []}
                        loading={loading}
                        pagination={false}
                        size="small"
                        scroll={{ x: 400 }}
                        locale={{ emptyText: t('billDetail.feeSummaryEmpty') }}
                    />
                </Card>
            )}

            <Card title={t('billDetail.exportHistoryTitle')}>
                <div className="mb-4 flex justify-end">
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={handleRefreshExportHistory}
                        loading={exportHistoryLoading}
                    >
                        {t('billDetail.refresh')}
                    </Button>
                </div>
                <Table<PortalBillExportRecord>
                    rowKey="taskId"
                    columns={exportRecordColumns}
                    dataSource={exportHistoryData?.portalBillExportHistory?.content || []}
                    loading={exportHistoryLoading}
                    pagination={{
                        current: exportPage + 1,
                        pageSize: exportPageSize,
                        total: exportHistoryData?.portalBillExportHistory?.totalElements || 0,
                        showSizeChanger: false,
                        onChange: (page) => setExportPage(page - 1),
                    }}
                    size="small"
                />
            </Card>

            <BillExportProgressModal
                open={exportOpen}
                taskIds={exportTaskIds}
                onClose={() => setExportOpen(false)}
                onCompleted={async () => {
                    message.success(t('billExport.completed'));
                    await refetchExportHistory();
                }}
            />
        </div>
    );
};

export default BillDetailPage;
